import React from "react";
import "./Form.css";
import Select from "react-select";
import translations from "../../data/translations";
import { parseLocationData } from "../../utils/ParsingLocationData";

const MAX_MESSAGE_LENGTH = 1000;

const LANGUAGES = [
  { code: "hebrew", label: "עברית" },
  { code: "english", label: "English" },
  { code: "arabic", label: "عربي" },
];

// Loaded on demand so the (large) city lists stay out of the main bundle.
const LOCATION_LOADERS = {
  english: () => import("../../data/jsons/citiescountrymappedenglish.json"),
  hebrew: () => import("../../data/jsons/citiescountrymappedhebrew.json"),
  arabic: () => import("../../data/jsons/citiescountrymappedarabic.json"),
};

const FieldError = ({ id, text, textAlign }) =>
  text ? (
    <div
      id={id}
      className="form-error"
      role="alert"
      aria-live="polite"
      style={{ textAlign }}
    >
      <span aria-hidden="true">⚠️</span>
      <span>{text}</span>
    </div>
  ) : null;

const GuestBookForm = ({
  name,
  setName,
  country,
  setCountry,
  city,
  setCity,
  message,
  setMessage,
  language,
  setLanguage,
  errors,
  loading,
  sendMessage,
}) => {
  const t = translations[language];
  const isRtl = language === "hebrew" || language === "arabic";
  const textAlign = isRtl ? "right" : "left";
  const dir = isRtl ? "rtl" : "ltr";

  const [locationData, setLocationData] = React.useState(null);

  React.useEffect(() => {
    let alive = true;
    setLocationData(null);
    (LOCATION_LOADERS[language] || LOCATION_LOADERS.english)().then((mod) => {
      if (alive) setLocationData(mod.default);
    });
    return () => {
      alive = false;
    };
  }, [language]);

  const { countries, citiesByCountry } = React.useMemo(
    () =>
      locationData
        ? parseLocationData(locationData)
        : { countries: [], citiesByCountry: {} },
    [locationData]
  );

  // Reset selects when language changes (labels change language too)
  React.useEffect(() => {
    setCountry(null);
    setCity(null);
  }, [language, setCountry, setCity]);

  const cityOptions =
    country && citiesByCountry[country.value]
      ? citiesByCountry[country.value]
      : [];

  const selectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: 48,
      borderRadius: "var(--radius-md)",
      background: "#fff",
      borderColor: state.isFocused ? "var(--primary-500)" : "var(--paper-300)",
      boxShadow: state.isFocused ? "var(--shadow-primary)" : "none",
      "&:hover": {
        borderColor: "var(--primary-500)",
      },
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? "var(--primary-500)"
        : state.isFocused
        ? "var(--primary-50)"
        : "white",
      color: state.isSelected ? "white" : "var(--neutral-900)",
      cursor: "pointer",
    }),
    menu: (base) => ({
      ...base,
      borderRadius: "var(--radius-md)",
      boxShadow: "var(--shadow-paper-lg)",
      overflow: "hidden",
    }),
    menuList: (base) => ({
      ...base,
      padding: 0,
    }),
  };

  const counterTone =
    message.length > MAX_MESSAGE_LENGTH
      ? "var(--error)"
      : message.length > MAX_MESSAGE_LENGTH * 0.9
      ? "var(--warning)"
      : "var(--ink-500)";

  return (
    <div className="gb-form" dir={dir}>
      <div className="form-language" aria-label="Language selection">
        {LANGUAGES.map((lang) => (
          <button
            key={lang.code}
            type="button"
            className={`language-chip ${
              language === lang.code ? "is-active" : ""
            }`}
            onClick={() => setLanguage(lang.code)}
            aria-pressed={language === lang.code}
            aria-label={`Set language to ${lang.label}`}
          >
            {lang.label}
          </button>
        ))}
      </div>

      <div className="form-field">
        <label className="form-label" htmlFor="guest-name">
          {t.name}
        </label>
        <input
          id="guest-name"
          type="text"
          className={`form-input ${errors.name ? "is-invalid" : ""}`}
          dir={dir}
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoComplete="name"
          aria-invalid={errors.name ? "true" : undefined}
          aria-describedby={errors.name ? "name-error" : undefined}
        />
        <FieldError id="name-error" text={errors.name} textAlign={textAlign} />
      </div>

      <div className="form-row">
        <div className="form-field">
          <label className="form-label" htmlFor="country-select">
            {t.country}
          </label>
          <Select
            options={countries}
            value={country}
            onChange={(selected) => {
              setCountry(selected);
              setCity(null);
            }}
            placeholder={t.country}
            isClearable
            isSearchable
            isLoading={!locationData}
            styles={selectStyles}
            noOptionsMessage={() => "No country found"}
            aria-invalid={!!errors.country}
            aria-describedby={errors.country ? "country-error" : undefined}
            inputId="country-select"
            aria-label={t.country}
          />
          <FieldError
            id="country-error"
            text={errors.country}
            textAlign={textAlign}
          />
        </div>

        <div className="form-field">
          <label className="form-label" htmlFor="city-select">
            {t.city}
          </label>
          <Select
            options={cityOptions}
            value={city}
            onChange={setCity}
            placeholder={t.city}
            isClearable
            isDisabled={!country}
            isSearchable
            styles={selectStyles}
            noOptionsMessage={() =>
              country ? "No city found" : "Select a country first"
            }
            aria-label={t.city}
            inputId="city-select"
          />
        </div>
      </div>

      <div className="form-field">
        <label className="form-label" htmlFor="guest-message">
          {t.message}
        </label>
        <textarea
          id="guest-message"
          rows={4}
          className={`form-input form-textarea ${
            errors.message ? "is-invalid" : ""
          }`}
          dir={dir}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          aria-invalid={errors.message ? "true" : undefined}
          aria-describedby={errors.message ? "message-error" : undefined}
        />
        <div className="form-meta">
          <FieldError
            id="message-error"
            text={errors.message}
            textAlign={textAlign}
          />
          <span className="form-counter" style={{ color: counterTone }}>
            {message.length}/{MAX_MESSAGE_LENGTH}
          </span>
        </div>
      </div>

      <button
        type="button"
        className="form-submit"
        disabled={loading}
        onClick={() => {
          if (!loading)
            sendMessage(
              name,
              country ? country.value : "",
              city ? city.value : "",
              message
            );
        }}
        aria-live="polite"
      >
        {loading ? <span className="form-spinner" aria-hidden="true" /> : t.send}
      </button>
    </div>
  );
};

export default GuestBookForm;
